import React, { type FC, useCallback, useEffect } from "react";

import { useIntl } from "react-intl";

import {
  Button,
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  MessageBox,
  PaginationBar,
  Panel,
  SkeletonLoader,
  SpacingVertical,
  Title,
} from "mailup-web-components";
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { EXTRA_COLUMN_AFTER_WIDTH } from "../model";
import {
  checkStartImportsCallback,
  pollingImportsCallback,
  searchToImportsCallback,
  stopImportsCallback,
} from "../recoil/callbacks";
import {
  importStatus,
  modalClauseImportState,
  tableToImportState,
} from "../recoil/state";

import { gainsightTrack } from "mailup-react-helpers";
import ToImportTableRow from "./ToImportTableRow";

const ToImportTable: FC = () => {
  const intl = useIntl();
  const searchToImports = useRecoilCallback(searchToImportsCallback, []);
  const [
    {
      loading,
      items,
      totalItems,
      pageSize,
      pageNumber,
      orderBy,
      orderDescending,
    },
    setTableToImportState,
  ] = useRecoilState(tableToImportState);

  const stopImports = useRecoilCallback(stopImportsCallback, []);
  const checkStartImports = useRecoilCallback(checkStartImportsCallback, []);

  const { status } = useRecoilValue(importStatus);

  const handleClickSort = (val: string) => {
    setTableToImportState((prev) => ({
      ...prev,
      orderBy: val,
      orderDescending: !orderDescending,
      pageNumber: 1,
    }));
    searchToImports(false, {
      orderBy: val,
      orderDescending: !orderDescending,
      pageNumber: 1,
    });
  };

  function handleChangePage(val: number) {
    setTableToImportState((prev) => ({
      ...prev,
      pageNumber: val,
    }));
    searchToImports(false, {
      pageNumber: val,
    });
  }

  function handleChangePageSize(val: number) {
    setTableToImportState((prev) => ({
      ...prev,
      pageSize: val,
    }));
    searchToImports(false, {
      pageSize: val,
    });
  }

  const openModalImport = () => {
    gainsightTrack("mup_recipients_click_startImport", {});

    checkStartImports();
  };

  const stopImport = () => {
    stopImports();
  };

  const heads = [
    {
      field: "Filename",
      label: intl.formatMessage({
        id: "Load.FileImportStatus.FileNameHeader",
      }),
      span: 4,
      sortable: true,
    },
    {
      label: intl.formatMessage({
        id: "Load.FileImportStatus.SizeHeader",
      }),
      span: 4,
      sortable: false,
    },
    {
      field: "Status",
      label: intl.formatMessage({
        id: "Load.FileImportStatus.StatusHeader",
      }),
      span: 4,
      sortable: true,
    },
  ];

  return totalItems > 0 ? (
    <>
      <MessageBox variant="help">
        {intl.formatMessage({ id: "Load.FileImportStatus.MsgInfo" })}
      </MessageBox>
      <SpacingVertical variant="16" />
      <Flex justifyContent="space-between" alignItems="end">
        <Title variant="M">
          {intl.formatMessage({ id: "Load.FileImportStatus.ActionRequest" })}
        </Title>
        {status === "stop" && (
          <Button disabled={loading} onClick={openModalImport}>
            {intl.formatMessage({ id: "Load.FileImportStatus.StartImport" })}
          </Button>
        )}
        {status === "running" && (
          <Button kind="danger" onClick={stopImport}>
            {intl.formatMessage({ id: "Load.FileImportStatus.StopBtn" })}
          </Button>
        )}
      </Flex>
      <SpacingVertical variant="16" />
      <Panel>
        <DataListHeader
          heads={heads}
          afterWidth={EXTRA_COLUMN_AFTER_WIDTH}
          orderByStatus={orderBy}
          orderDescendingStatus={orderDescending}
          handleChangeColumnsOrderCallback={handleClickSort}
        />
        <div>
          {loading
            ? Array.from({
                length: totalItems < 5 ? totalItems : pageSize,
              }).map((_, i) => (
                <DataListRow key={i}>
                  <Grid cols={12}>
                    <GridCell span={4}>
                      <SkeletonLoader />
                    </GridCell>
                    <GridCell span={4}>
                      <SkeletonLoader />
                    </GridCell>
                    <GridCell span={4}>
                      <SkeletonLoader />
                    </GridCell>
                  </Grid>
                  <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH} />
                </DataListRow>
              ))
            : items.map((item, i) => <ToImportTableRow key={i} item={item} />)}
        </div>

        <PaginationBar
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalItems={totalItems}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
        />
      </Panel>
    </>
  ) : (
    <></>
  );
};

export default ToImportTable;
